import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

export type TeamIntakeQuestion = {
  id: string;
  type: 'text' | 'select';
  label: string;
  required?: boolean;
  options?: string[];
};

export type TeamIntakeTemplate = {
  id: string;
  club_id?: string | null;
  team_id?: string | null;
  title: string;
  description?: string | null;
  questions: TeamIntakeQuestion[];
  is_active: boolean;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TeamIntakeRun = {
  id: string;
  template_id: string;
  public_token?: string | null;
  club_id?: string | null;
  team_id: string;
  season?: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'closed' | 'cancelled';
  due_date?: string | null;
  sent_at?: string | null;
  closed_at?: string | null;
  access_code_hint?: string | null;
  access_code_expires_at?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TeamIntakeResponse = {
  id: string;
  run_id: string;
  club_id?: string | null;
  team_id: string;
  player_id: string;
  answers: Record<string, unknown>;
  status: 'draft' | 'submitted' | 'reopened';
  submitted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type PublicIntakeWelcome = {
  id: string;
  public_token: string;
  title: string;
  season?: string | null;
  status: string;
  due_date?: string | null;
  access_code_hint?: string | null;
};

export type PublicIntakeSession = {
  run: PublicIntakeWelcome;
  questions: TeamIntakeQuestion[];
};

export type PublicIntakePlayer = {
  id: string;
  name: string;
  number?: string | null;
};

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    const detail = record.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) return detail;
    const error = record.error;
    if (typeof error === 'string' && error.trim().length > 0) return error;
    const message = record.message;
    if (typeof message === 'string' && message.trim().length > 0) return message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const extractList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.items)) return record.items as unknown[];
    if (Array.isArray(record.results)) return record.results as unknown[];
    if (Array.isArray(record.data)) return record.data as unknown[];
  }
  return [];
};

const teamIntakeRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Team Intake API request failed (${response.status})`));
  }

  return extractData(payload);
};

const publicTeamIntakeRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) throw new Error('VITE_AI_API_URL is not configured');

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': getEnv('VITE_AI_API_KEY', ''),
      ...(options.headers || {}),
    },
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractMessage(payload, `Public Team Intake API request failed (${response.status})`));
  }
  return extractData(payload);
};

export const getPublicIntakeApi = async (publicToken: string): Promise<PublicIntakeWelcome> =>
  await publicTeamIntakeRequest(`/api/v1/team-intake/public/${encodeURIComponent(publicToken)}`, {
    method: 'GET',
  }) as PublicIntakeWelcome;

export const verifyPublicIntakeCodeApi = async (
  publicToken: string,
  accessCode: string,
): Promise<PublicIntakeSession> =>
  await publicTeamIntakeRequest(`/api/v1/team-intake/public/${encodeURIComponent(publicToken)}/verify-code`, {
    method: 'POST',
    body: JSON.stringify({ access_code: accessCode }),
  }) as PublicIntakeSession;

export const searchPublicIntakePlayersApi = async (params: {
  publicToken: string;
  accessCode: string;
  search: string;
}): Promise<PublicIntakePlayer[]> => {
  const query = new URLSearchParams({ access_code: params.accessCode, search: params.search });
  return await publicTeamIntakeRequest(
    `/api/v1/team-intake/public/${encodeURIComponent(params.publicToken)}/players?${query.toString()}`,
    { method: 'GET' },
  ) as PublicIntakePlayer[];
};

export const submitPublicIntakeResponseApi = async (params: {
  publicToken: string;
  accessCode: string;
  playerId: string;
  answers: Record<string, unknown>;
}): Promise<void> => {
  await publicTeamIntakeRequest(
    `/api/v1/team-intake/public/${encodeURIComponent(params.publicToken)}/responses`,
    {
      method: 'POST',
      body: JSON.stringify({
        access_code: params.accessCode,
        player_id: params.playerId,
        answers: params.answers,
      }),
    },
  );
};

export const listTeamIntakeTemplatesApi = async (params: {
  team_id?: string | null;
  club_id?: string | null;
  include_global?: boolean;
  include_inactive?: boolean;
  limit?: number;
  offset?: number;
}): Promise<TeamIntakeTemplate[]> => {
  const query = new URLSearchParams();
  if (params.team_id) query.set('team_id', params.team_id);
  if (params.club_id) query.set('club_id', params.club_id);
  if (typeof params.include_global === 'boolean') query.set('include_global', String(params.include_global));
  if (typeof params.include_inactive === 'boolean') query.set('include_inactive', String(params.include_inactive));
  if (typeof params.limit === 'number') query.set('limit', String(Math.max(1, params.limit)));
  if (typeof params.offset === 'number') query.set('offset', String(Math.max(0, params.offset)));

  const payload = await teamIntakeRequest(`/api/v1/team-intake/templates?${query.toString()}`, { method: 'GET' });
  return extractList(payload) as TeamIntakeTemplate[];
};

export const createTeamIntakeTemplateApi = async (
  payload: Record<string, unknown>
): Promise<TeamIntakeTemplate> => {
  return await teamIntakeRequest('/api/v1/team-intake/templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as TeamIntakeTemplate;
};

export const updateTeamIntakeTemplateApi = async (
  id: string,
  payload: Record<string, unknown>
): Promise<TeamIntakeTemplate> => {
  return await teamIntakeRequest(`/api/v1/team-intake/templates/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }) as TeamIntakeTemplate;
};

export const listTeamIntakeRunsApi = async (params: {
  team_id: string;
  status?: string | null;
  limit?: number;
  offset?: number;
}): Promise<TeamIntakeRun[]> => {
  const query = new URLSearchParams({ team_id: params.team_id });
  if (params.status) query.set('status', params.status);
  if (typeof params.limit === 'number') query.set('limit', String(Math.max(1, params.limit)));
  if (typeof params.offset === 'number') query.set('offset', String(Math.max(0, params.offset)));

  const payload = await teamIntakeRequest(`/api/v1/team-intake/runs?${query.toString()}`, { method: 'GET' });
  return extractList(payload) as TeamIntakeRun[];
};

export const createTeamIntakeRunApi = async (payload: Record<string, unknown>): Promise<TeamIntakeRun> => {
  return await teamIntakeRequest('/api/v1/team-intake/runs', {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as TeamIntakeRun;
};

export const updateTeamIntakeRunApi = async (
  id: string,
  payload: Record<string, unknown>
): Promise<TeamIntakeRun> => {
  return await teamIntakeRequest(`/api/v1/team-intake/runs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }) as TeamIntakeRun;
};

export const listTeamIntakeResponsesApi = async (params: {
  run_id?: string | null;
  team_id?: string | null;
  player_id?: string | null;
  limit?: number;
  offset?: number;
  include_answers?: boolean;
}): Promise<TeamIntakeResponse[]> => {
  const query = new URLSearchParams();
  if (params.run_id) query.set('run_id', params.run_id);
  if (params.team_id) query.set('team_id', params.team_id);
  if (params.player_id) query.set('player_id', params.player_id);
  if (typeof params.limit === 'number') query.set('limit', String(Math.max(1, params.limit)));
  if (typeof params.offset === 'number') query.set('offset', String(Math.max(0, params.offset)));

  if (params.include_answers === false) query.set('include_answers', 'false');

  const payload = await teamIntakeRequest(`/api/v1/team-intake/responses?${query.toString()}`, { method: 'GET' });
  return extractList(payload) as TeamIntakeResponse[];
};
